/**
 * AP-6 — schlanker Rahmen für die drei Filterfenster (FM-WIN-15/16/17).
 * "Schließen versteckt das Fenster nur" (FM-WIN-15) — daher kein
 * destroy/remove, nur `hide()`; Inhalt und Formularzustand bleiben erhalten.
 */

import './FloatingPanel.css';

export interface FloatingPanel {
  element: HTMLElement;
  body: HTMLElement;
  show(): void;
  hide(): void;
  readonly visible: boolean;
  /** Sichtbarer Titel — die Dock-Titelzeile von AP-12 zeigt ihn an. */
  readonly title: string;
  /**
   * AP-12 — Gegenstück zu `Win11Theme::SetEmbedded()`: hängt das Fenster in
   * einen fremden Container um und legt die Popup-Darstellung ab.
   */
  embed(container: HTMLElement): void;
  /** Zurück zum freien Popup (`SetEmbedded(..., false)`), bleibt versteckt. */
  release(): void;
  readonly embedded: boolean;
  /** Meldet das Schließen über die eigene ×-Schaltfläche. */
  onHide(listener: () => void): void;
}

let cascadeOffset = 0;

/**
 * Wird von `AppShell` vor `show()` gesetzt, wenn der Single-Window-Mode aktiv
 * ist. Entspricht `CanMonitorApp::OpenView()`, das zwischen `EmbedView()` und
 * `showAsPopup()` entscheidet — die View selbst kennt den Unterschied nicht.
 */
let pendingEmbed: ((panel: FloatingPanel) => void) | null = null;
/** Wird bei jedem Anfassen hochgezaehlt, damit das aktive Fenster oben liegt. */
let topZIndex = 300;

/** Nur der nächste `show()`-Aufruf wird angedockt, danach gilt wieder Popup. */
export function embedNextShow(handler: (panel: FloatingPanel) => void): void {
  pendingEmbed = handler;
}

export function cancelPendingEmbed(): void {
  pendingEmbed = null;
}

export function createFloatingPanel(title: string): FloatingPanel {
  const element = document.createElement('div');
  element.className = 'cm-panel-window';
  element.style.display = 'none';

  const header = document.createElement('div');
  header.className = 'cm-panel-window__header';
  const titleEl = document.createElement('span');
  titleEl.textContent = title;
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'cm-panel-window__close';
  closeBtn.textContent = '\u00d7';
  closeBtn.title = 'Schlie\u00dfen';
  closeBtn.addEventListener('click', () => hide());
  header.append(titleEl, closeBtn);

  const body = document.createElement('div');
  body.className = 'cm-panel-window__body';

  element.append(header, body);
  document.body.append(element);

  const hideListeners: (() => void)[] = [];
  let embeddedIn: HTMLElement | null = null;
  /** Inline-Wunschmasse des Popups, waehrend des Andockens geparkt. */
  let popupWidth = '';
  let popupHeight = '';
  /** Sobald der Nutzer geschoben hat, positioniert `show()` nicht mehr neu. */
  let hasCustomPosition = false;
  let dragPointerId: number | null = null;
  let dragOffsetX = 0;
  let dragOffsetY = 0;

  function bringToFront(): void {
    if (embeddedIn !== null) return;
    topZIndex += 1;
    element.style.zIndex = String(topZIndex);
  }

  /** Haelt das Fenster im sichtbaren Bereich, damit die Kopfzeile greifbar bleibt. */
  function moveTo(x: number, y: number): void {
    const maxX = Math.max(8, window.innerWidth - element.offsetWidth - 8);
    const maxY = Math.max(8, window.innerHeight - element.offsetHeight - 8);
    element.style.left = `${Math.round(Math.min(Math.max(8, x), maxX))}px`;
    element.style.top = `${Math.round(Math.min(Math.max(8, y), maxY))}px`;
  }

  header.addEventListener('pointerdown', (event: PointerEvent) => {
    bringToFront();
    if (embeddedIn !== null) return;
    if (event.target instanceof Element && event.target.closest('.cm-panel-window__close') !== null) {
      return;
    }
    const rect = element.getBoundingClientRect();
    dragOffsetX = event.clientX - rect.left;
    dragOffsetY = event.clientY - rect.top;
    dragPointerId = event.pointerId;
    header.classList.add('cm-panel-window__header--dragging');
    // Bewusst am window statt per setPointerCapture am Header: der Zeiger
    // verlaesst die schmale Kopfzeile beim schnellen Ziehen sofort.
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    event.preventDefault();
  });

  function onDragMove(event: PointerEvent): void {
    if (dragPointerId !== event.pointerId) return;
    hasCustomPosition = true;
    moveTo(event.clientX - dragOffsetX, event.clientY - dragOffsetY);
  }

  function endDrag(event: PointerEvent): void {
    if (dragPointerId !== event.pointerId) return;
    dragPointerId = null;
    header.classList.remove('cm-panel-window__header--dragging');
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', endDrag);
    window.removeEventListener('pointercancel', endDrag);
  }

  element.addEventListener('pointerdown', bringToFront);

  window.addEventListener('resize', () => {
    if (embeddedIn !== null || element.style.display === 'none') return;
    moveTo(element.offsetLeft, element.offsetTop);
  });

  // Fenster wachsen nach dem Oeffnen weiter (Listen fuellen sich erst mit dem
  // ersten Snapshot) — sonst ragt der Zuwachs unten/rechts hinaus.
  new ResizeObserver(() => {
    if (embeddedIn !== null || element.style.display === 'none') return;
    moveTo(element.offsetLeft, element.offsetTop);
  }).observe(element);

  function embed(container: HTMLElement): void {
    if (embeddedIn === container) {
      element.style.display = 'flex';
      return;
    }
    embeddedIn = container;
    element.classList.add('cm-panel-window--embedded');
    // Die Popup-Geometrie muss weg, sonst positioniert sie das Fenster auch
    // im Container absolut.
    element.style.left = '';
    element.style.top = '';
    element.style.zIndex = '';
    // Eine als Popup gewuenschte Wunschbreite/-hoehe (z. B. SignalPlot:
    // `style.width = 860px`) haelt das Fenster sonst als Inline-Stil auf
    // dieser Breite fest — angedockt soll es die Dockflaeche fuellen.
    popupWidth = element.style.width;
    popupHeight = element.style.height;
    element.style.width = '';
    element.style.height = '';
    element.style.display = 'flex';
    container.append(element);
  }

  function release(): void {
    if (embeddedIn === null) return;
    embeddedIn = null;
    element.classList.remove('cm-panel-window--embedded');
    element.style.width = popupWidth;
    element.style.height = popupHeight;
    element.style.display = 'none';
    hasCustomPosition = false; // nach dem Ausdocken wieder frisch positionieren
    document.body.append(element);
  }

  function show(): void {
    const embedHandler = pendingEmbed;
    pendingEmbed = null;
    if (embedHandler !== null) {
      embedHandler(panel);
      return;
    }
    if (embeddedIn !== null) {
      element.style.display = 'flex';
      return;
    }
    element.style.display = 'flex';
    if (hasCustomPosition) {
      // Vom Nutzer geschobene Fenster bleiben, wo sie sind — nur nachklemmen,
      // falls das Browserfenster inzwischen kleiner geworden ist.
      moveTo(element.offsetLeft, element.offsetTop);
      bringToFront();
      return;
    }
    const offset = (cascadeOffset % 6) * 24;
    cascadeOffset += 1;
    // Ueber moveTo(), damit ein hohes/breites Fenster nicht unten oder rechts
    // aus dem Browserfenster herausragt.
    moveTo(
      Math.round(window.innerWidth / 2 - element.offsetWidth / 2) + offset,
      Math.round(window.innerHeight / 2 - element.offsetHeight / 2) + offset,
    );
    bringToFront();
  }

  function hide(): void {
    element.style.display = 'none';
    for (const listener of hideListeners) listener();
  }

  const panel: FloatingPanel = {
    element,
    body,
    show,
    hide,
    get visible() {
      return element.style.display !== 'none';
    },
    get title() {
      return title;
    },
    embed,
    release,
    get embedded() {
      return embeddedIn !== null;
    },
    onHide(listener: () => void) {
      hideListeners.push(listener);
    },
  };
  return panel;
}
